import sourceSnapshot from
  "../../data/overrides/transit/vilnius-public-transport-stops.json" with {
    type: "json",
  };
import furnitureSnapshot from
  "../../data/overrides/transit/vilnius-major-stop-furniture.json" with {
    type: "json",
  };

import type { LocalHorizontal } from "./coordinates.ts";
import type { RuntimeWorldTilePayloadV2 } from "./schema/runtimeTile.ts";

export type VilniusPublicTransportMode = "bus" | "trolleybus";

export type VilniusStopFurnitureProfile =
  | "glass-shelter-display"
  | "glass-shelter"
  | "shelter-no-bench"
  | "bench-only"
  | "airport-shelter"
  | "historic-pole";

export interface VilniusStopFurnitureEvidence {
  readonly stopId: string;
  readonly osmNodeId: number;
  readonly distanceM: number;
  readonly profile: VilniusStopFurnitureProfile;
  readonly shelter: boolean;
  readonly bench: boolean;
  readonly bin: boolean;
  readonly tactilePaving: boolean;
  readonly passengerInformationDisplay: boolean;
  readonly lit: boolean;
}

export interface VilniusPublicTransportStopSource {
  readonly id: string;
  readonly stopId: string;
  readonly name: string;
  readonly description: string;
  readonly stopUrl: string;
  readonly modes: readonly VilniusPublicTransportMode[];
  readonly routeShortNames: readonly string[];
  readonly wgs84: Readonly<{
    latitude: number;
    longitude: number;
  }>;
  readonly lks94: Readonly<{
    easting: number;
    northing: number;
  }>;
}

export interface TileBusStopSource extends VilniusPublicTransportStopSource {
  /** Metres relative to the owning tile origin. */
  readonly position: LocalHorizontal;
  /** OSM-tagged furniture only; absent means the base JUDU stop pole. */
  readonly furniture: Readonly<VilniusStopFurnitureEvidence> | null;
}

interface BusStopSnapshot {
  readonly schemaVersion: number;
  readonly stopCount: number;
  readonly stops: readonly VilniusPublicTransportStopSource[];
}

interface BusStopFurnitureSnapshot {
  readonly schemaVersion: number;
  readonly capturedAt: string;
  readonly source: Readonly<{
    label: string;
    url: string;
    license: string;
    method: string;
  }>;
  readonly stops: readonly VilniusStopFurnitureEvidence[];
}

const SHELTER_PROFILES = new Set<VilniusStopFurnitureProfile>([
  "glass-shelter-display",
  "glass-shelter",
  "shelter-no-bench",
  "airport-shelter",
]);

function validFurnitureProfile(
  stop: Readonly<VilniusStopFurnitureEvidence>,
): boolean {
  if (stop.shelter !== SHELTER_PROFILES.has(stop.profile)) {
    return false;
  }
  if (stop.profile === "glass-shelter-display") {
    return stop.bench && stop.passengerInformationDisplay;
  }
  if (stop.profile === "shelter-no-bench") {
    return !stop.bench && !stop.passengerInformationDisplay;
  }
  if (stop.profile === "bench-only") {
    return !stop.shelter && stop.bench;
  }
  if (stop.profile === "historic-pole") {
    return !stop.shelter && !stop.bench && !stop.passengerInformationDisplay;
  }
  return true;
}

const snapshot = sourceSnapshot as BusStopSnapshot;
const furniture = furnitureSnapshot as BusStopFurnitureSnapshot;

if (
  snapshot.schemaVersion !== 1 ||
  snapshot.stopCount !== snapshot.stops.length
) {
  throw new Error("Invalid Vilnius public-transport stop source snapshot");
}

if (
  furniture.schemaVersion !== 1 ||
  new Set(furniture.stops.map((stop) => stop.stopId)).size !==
    furniture.stops.length ||
  new Set(furniture.stops.map((stop) => stop.osmNodeId)).size !==
    furniture.stops.length ||
  furniture.stops.some((stop) =>
    !stop.stopId.startsWith("judu:stop:") ||
    stop.distanceM < 0 ||
    stop.distanceM > 15 ||
    !validFurnitureProfile(stop)
  )
) {
  throw new Error("Invalid Vilnius stop-furniture evidence snapshot");
}

const FURNITURE_BY_STOP_ID = new Map(
  furniture.stops.map((stop) => [stop.stopId, Object.freeze({ ...stop })]),
);

export const VILNIUS_STOP_FURNITURE_EVIDENCE = Object.freeze({
  capturedAt: furniture.capturedAt,
  source: Object.freeze({ ...furniture.source }),
  stops: Object.freeze([...FURNITURE_BY_STOP_ID.values()]),
});

export const VILNIUS_PUBLIC_TRANSPORT_STOP_SOURCES:
  readonly Readonly<VilniusPublicTransportStopSource>[] = Object.freeze(
    snapshot.stops.map((stop) => Object.freeze({
      ...stop,
      modes: Object.freeze([...stop.modes]),
      routeShortNames: Object.freeze([...stop.routeShortNames]),
    })),
  );

export function busStopSourcesForTile(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
): readonly Readonly<TileBusStopSource>[] {
  const origin = tile.localOriginLks94;
  return VILNIUS_PUBLIC_TRANSPORT_STOP_SOURCES.flatMap((stop) => {
    const globalX = stop.lks94.easting - origin.easting;
    const globalZ = origin.northing - stop.lks94.northing;
    if (
      globalX < tile.bounds.minX ||
      globalX >= tile.bounds.maxX ||
      globalZ < tile.bounds.minZ ||
      globalZ >= tile.bounds.maxZ
    ) {
      return [];
    }
    return [{
      ...stop,
      position: {
        x: globalX - tile.origin.x,
        z: globalZ - tile.origin.z,
      },
      furniture: FURNITURE_BY_STOP_ID.get(stop.id) ?? null,
    }];
  });
}
